import { uploadMedia } from './mediaUpload';
import { croppedItem } from './crop';
import { syncImagesFields } from './products';

// Background queue for video crops. Encoding happens in-browser and can take minutes, so the
// crop window hands the work off here and closes; jobs run one at a time (there's a single
// ffmpeg instance) and each result is swapped into its product by ID when it lands — via the
// patcher App registers, which works against current state wherever the person has gone since.

let jobs = [];
let running = false;
let patcher = null;
const subs = new Set();
let seq = 0;

const emit = () => subs.forEach(fn => fn(jobs));
const update = (id, patch) => { jobs = jobs.map(j => j.id === id ? { ...j, ...patch } : j); emit(); };

export const setMediaPatcher = (fn) => { patcher = fn; };
export const subscribeCropJobs = (fn) => { subs.add(fn); fn(jobs); return () => subs.delete(fn); };
export const dismissCropJob = (id) => { jobs = jobs.filter(j => j.id !== id); emit(); };
export const hasActiveCropJobs = () => jobs.some(j => j.status !== 'done' && j.status !== 'error');

/**
 * Queues one crop. `make({ onStage, onProgress })` produces { file, crop }; `mediaUrl` is the
 * item it replaces (matched at swap-in time, so an item removed meanwhile is just skipped).
 */
export function enqueueCrop({ productId, label, mediaUrl, make }) {
  jobs = [...jobs, { id: ++seq, productId, label, mediaUrl, make, status: 'queued', progress: 0 }];
  emit();
  run();
}

async function run() {
  if (running) return;
  running = true;
  try {
    let job;
    while ((job = jobs.find(j => j.status === 'queued'))) await runJob(job);
  } finally {
    running = false;
  }
}

async function runJob(job) {
  const { id } = job;
  try {
    const { file, crop } = await job.make({
      onStage: stage => update(id, { status: stage }),
      onProgress: progress => update(id, { status: 'encoding', progress }),
    });
    update(id, { status: 'uploading' });
    const uploaded = await uploadMedia(file, job.productId);
    let found = false;
    const ok = patcher?.(job.productId, p => {
      const images = (p.images || []).map(m => {
        if (m.url !== job.mediaUrl) return m;
        found = true;
        return croppedItem(m, uploaded, crop);
      });
      return { ...p, images, ...syncImagesFields(images), updatedAt: new Date().toISOString() };
    });
    if (!ok || !found) throw new Error('The video was removed from the product before the crop finished');
    update(id, { status: 'done', progress: 1 });
  } catch (err) {
    update(id, { status: 'error', error: err.message || String(err) });
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', e => {
    if (hasActiveCropJobs()) { e.preventDefault(); e.returnValue = ''; }
  });
}
